import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Field, Segment, TextArea } from "../components/ui";
import AppHeader from "../components/AppHeader";

const TYPES = [
  { key: "in_person", label: "In Person" },
  { key: "over_call", label: "Over Call" },
  { key: "google_meet", label: "Google Meet" },
];
const TEAMS = [
  { key: "sampling", label: "Sampling" },
  { key: "production", label: "Production" },
  { key: "general", label: "General" },
];

export default function MeetingNew() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const today = new Date().toISOString().slice(0, 10);

  const [form, setForm] = useState({
    customer_id: sp.get("customer_id") || "",
    customer_name: sp.get("customer_name") || "",
    company_name: "",
    customer_email: "",
    customer_phone: "",
    date: today,
    time: "",
    meeting_type: "google_meet",
    team: "general",
    notes: "",
  });
  const [allowed, setAllowed] = useState([]);
  const [booked, setBooked] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get(`/meetings/slots?date=${form.date}`)
      .then((r) => {
        const al = r.allowed || [];
        setAllowed(al);
        setBooked(r.booked || []);
        setBlocked(r.blocked || []);
        // Auto-pick the first free slot when date changes
        const taken = new Set([...(r.booked || []), ...(r.blocked || [])]);
        const firstFree = al.find((s) => !taken.has(s)) || "";
        setForm((f) => ({ ...f, time: firstFree }));
      })
      .catch(() => { setAllowed([]); setBooked([]); setBlocked([]); });
  }, [form.date]);

  const taken = new Set([...booked, ...blocked]);

  const submit = async () => {
    setError("");
    if (!form.customer_name) { setError("Please enter customer name"); return; }
    if (!allowed.length) { setError("We're closed that day. Pick a weekday (Mon–Fri)."); return; }
    if (!form.time) { setError("Please pick an available time slot."); return; }
    if (taken.has(form.time)) { setError("Slot already taken. Pick another time."); return; }
    try {
      setBusy(true);
      await api.post("/meetings", form);
      navigate("/meetings");
    } catch (e) { setError(e?.message || "Could not book"); } finally { setBusy(false); }
  };

  return (
    <>
      <AppHeader title="Book Meeting" back />
      <div className="sg-main sg-fade-in">
        <Field label="Company / Brand" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="Acme Apparels" data-testid="meeting-company" />
        <Field label="Contact person" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} placeholder="Riya Sharma" data-testid="meeting-customer" />
        <Field label="Email" type="email" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} placeholder="contact@brand.com" />
        <Field label="Phone" type="tel" value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} placeholder="+91 98xxx xxxxx" />
        <Field label="Date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="meeting-date" />

        <label className="sg-label">Time slot</label>
        {allowed.length === 0 ? (
          <div className="sg-card" style={{ color: "var(--muted)", fontSize: 13, textAlign: "center", marginBottom: 16 }} data-testid="slot-closed">
            We&apos;re closed on this day. Please pick a weekday (Mon–Fri).
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 16 }}>
            {allowed.map((slot) => {
              const isTaken = taken.has(slot);
              const active = form.time === slot;
              return (
                <button
                  key={slot}
                  type="button"
                  disabled={isTaken}
                  onClick={() => set("time", slot)}
                  className={`sg-slot ${active ? "sg-slot-active" : ""} ${isTaken ? "sg-slot-taken" : ""}`}
                  data-testid={`slot-${slot}`}
                  style={{ minWidth: 0 }}
                >{slot}</button>
              );
            })}
          </div>
        )}

        <label className="sg-label">Meeting type</label>
        <Segment options={TYPES} value={form.meeting_type} onChange={(v) => set("meeting_type", v)} testIdPrefix="mtype" />

        <label className="sg-label">With team</label>
        <Segment options={TEAMS} value={form.team} onChange={(v) => set("team", v)} testIdPrefix="team" />

        <TextArea label="Notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="meeting-notes" />

        {error && <div style={{ color: "var(--danger)", fontSize: 13, marginBottom: 12 }}>{error}</div>}

        <button className="sg-btn sg-btn-primary" onClick={submit} disabled={busy || !allowed.length} data-testid="meeting-save" style={{ width: "100%", marginTop: 12 }}>
          {busy ? "Booking..." : "Confirm Meeting"}
        </button>
      </div>
    </>
  );
}
